import { Injectable, effect, signal, computed, Signal, WritableSignal, Injector, runInInjectionContext } from '@angular/core';
import { Subject } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { LayoutConfig, LayoutState, MenuChangeEvent, TabCloseEvent } from './layout.models';

export * from './layout.models';

/** Clé de stockage de l'apparence choisie ('dark' | 'light'). */
const DARK_THEME_STORAGE_KEY = 'aboneo-apparence';

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    // --- Configuration & State ---
    protected _config: LayoutConfig = {
        preset: 'Aura',
        primary: 'emerald',
        surface: null,
        darkTheme: false,
        menuMode: 'slim',
        layoutTheme: 'colorScheme'
    };

    protected _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
        rightMenuActive: false,
        topbarMenuActive: false,
        sidebarActive: false,
        activeMenuItem: null,
        overlaySubmenuActive: false,
        // profileSidebarVisible init handled in front2 usage
    };

    layoutConfig = signal<LayoutConfig>(this._config);
    layoutState = signal<LayoutState>(this._state);

    protected configUpdate = new Subject<LayoutConfig>();
    protected overlayOpen = new Subject<any>();
    protected resetSource = new Subject();

    // --- Observable Sources ---
    private menuSource = new Subject<MenuChangeEvent>();
    private tabOpen = new Subject<MenuItem>();
    private tabClose = new Subject<TabCloseEvent>();

    tabs: MenuItem[] = [];

    resetSource$ = this.resetSource.asObservable();
    configUpdate$ = this.configUpdate.asObservable();
    overlayOpen$ = this.overlayOpen.asObservable();
    menuSource$ = this.menuSource.asObservable();
    tabOpen$ = this.tabOpen.asObservable();
    tabClose$ = this.tabClose.asObservable();

    // --- computed signals ---
    isSidebarActive: Signal<boolean> = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);
    isDarkTheme: Signal<boolean> = computed(() => this.layoutConfig().darkTheme);
    isOverlay: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'overlay');
    isSlim: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'slim');
    isSlimPlus: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'slim-plus');
    isHorizontal: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'horizontal');
    transitionComplete: WritableSignal<boolean> = signal<boolean>(false);

    isSidebarStateChanged = computed(() => {
        const layoutConfig = this.layoutConfig();
        return layoutConfig.menuMode === 'horizontal' || layoutConfig.menuMode === 'slim' || layoutConfig.menuMode === 'slim-plus';
    });

    private initialized = false;

    constructor(private injector: Injector) {
        this.initEffects();
    }

    private initEffects() {
        runInInjectionContext(this.injector, () => {
            effect(() => {
                const config = this.layoutConfig();
                if (config) {
                    this.onConfigUpdate();
                }
            });

            effect(() => {
                const config = this.layoutConfig();
                if (!this.initialized || !config) {
                    this.initialized = true;
                    return;
                }
                this.handleDarkModeTransition(config);
            });

            effect(() => {
                if (this.isSidebarStateChanged()) {
                    this.reset();
                }
            });
        });
    }

    private handleDarkModeTransition(config: LayoutConfig): void {
        if ((document as any).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
            this.onTransitionEnd();
        }
    }

    private startViewTransition(config: LayoutConfig): void {
        const transition = (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });

        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => { });
    }

    /**
     * Apparence mémorisée par l'utilisateur ; à défaut, préférence du système.
     * (Même principe que Prodicee : bascule dans la barre du haut, choix conservé.)
     */
    readStoredDarkTheme(): boolean {
        try {
            const stored = localStorage.getItem(DARK_THEME_STORAGE_KEY);
            if (stored === 'dark') return true;
            if (stored === 'light') return false;
        } catch {
            // localStorage indisponible : on retombe sur la préférence système.
        }
        return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    }

    /** Bascule claire / sombre depuis l'interface, et mémorise le choix. */
    setDarkTheme(dark: boolean): void {
        this.layoutConfig.update((config) => ({ ...config, darkTheme: dark }));
        try {
            localStorage.setItem(DARK_THEME_STORAGE_KEY, dark ? 'dark' : 'light');
        } catch {
            // Choix conservé pour la session seulement.
        }
    }

    toggleDarkMode(config?: LayoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));
            if (this.layoutState().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive }));
        } else {
            this.layoutState.update((prev) => ({ ...prev, staticMenuMobileActive: !this.layoutState().staticMenuMobileActive }));
            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());
    }

    reset() {
        this.resetSource.next(true);
    }

    onOverlaySubmenuOpen() {
        this.overlayOpen.next(null);
    }

    showConfigSidebar() {
        this.layoutState.update((prev) => ({ ...prev, configSidebarVisible: true }));
    }

    hideConfigSidebar() {
        this.layoutState.update((prev) => ({ ...prev, configSidebarVisible: false }));
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    showProfileSidebar() {
        this.layoutState.update((prev) => ({ ...prev, profileSidebarVisible: true }));
    }

    onTabOpen(value: MenuItem) {
        this.tabOpen.next(value);
    }

    openTab(value: MenuItem) {
        this.tabs = [...this.tabs, value];
    }

    onTabClose(value: MenuItem, index: number) {
        this.tabClose.next({ tab: value, index: index });
    }

    closeTab(index: number) {
        this.tabs.splice(index, 1);
        this.tabs = [...this.tabs];
    }
}
